import { CartAddEvent } from '@theme/events';

const money = (cents) => `$${(cents / 100).toFixed(2)}`;
const httpsUrl = (u) => (u && u.startsWith('//') ? `https:${u}` : u || '');

/**
 * <bundle-builder> — multi-step, multi-bundle builder.
 *
 * - Mỗi step 1+ collection (tab phân loại). Sản phẩm tải lazy:
 *   Browse: /collections/<handle>?view=bb&sort_by=best-selling
 *   Search: paginate đúng collection đang chọn + lọc theo query client-side
 *           (xếp prefix-match trước), KHÔNG search toàn store.
 * - Khách dựng 1 "draft", đủ món → thành 1 bundle; có thể "ADD ANOTHER BUNDLE".
 * - Giá: fixed (combo cố định) hoặc bxgy (chỉ định step free). Tiền thật do
 *   Shopify Function tính ở checkout dựa trên thuộc tính `_bundle_*` của giỏ.
 */
class BundleBuilder extends HTMLElement {
  #abort = null;
  #io = null;

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.bundleName = this.dataset.bundleName || 'BUNDLE';
    this.bundlePriceCents = parseInt(this.dataset.bundlePriceCents, 10) || 0;
    this.addLabel = this.dataset.addLabel || 'ADD';
    this.searchingLabel = this.dataset.searchingLabel || 'Searching products…';
    this.savingsLabel = this.dataset.savingsLabel || 'You save';
    this.currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    this.startedTracked = false;
    this.pricingType = this.dataset.pricingType === 'bxgy' ? 'bxgy' : 'fixed';
    this.freeSlots = (this.dataset.freeSlots || '')
      .split(',')
      .map((s) => parseInt(s, 10))
      .filter((n) => Number.isInteger(n));

    // "Buy more, save more": bundle thứ 2 trở đi giảm THÊM % trên giá bundle đó.
    // index 1 (bundle 2) -> tiers[0], index 2 -> tiers[1], ... chốt ở mức cuối.
    this.extraTiers = (this.dataset.extraTiers || '')
      .split(',')
      .map((s) => parseFloat(s))
      .filter((n) => Number.isFinite(n) && n > 0);
    this.bxmsLabel = this.dataset.buyMoreLabel || 'Buy more, save more';
    // Note "limited time" (chỉ có khi offer bật countdown) -> gắn vào line item để cart hiện.
    this.limitedNote = this.dataset.limitedNote || '';
    // Collection-mode: bỏ lớp brand — mỗi step load THẲNG collection (qua tabs nếu nhiều).
    this.collectionMode = this.dataset.collectionMode === '1';

    // Free shipping unlock (nhử add bundle ở màn cuối). Dựa trên TỔNG phải trả >= ngưỡng (cents).
    this.fsEnabled = this.dataset.fsEnabled === '1';
    this.fsThreshold = parseInt(this.dataset.fsThreshold, 10) || 0;
    this.fsLocked = this.dataset.fsLocked || '';
    this.fsUnlocked = this.dataset.fsUnlocked || '';
    // Nudge hiện trong CART khi chỉ có 1 bundle (gợi ý add bundle 2). Gắn vào line item.
    const t0 = this.extraTiers && this.extraTiers.length ? this.extraTiers[0] : 0;
    this.fsNudge = this.fsEnabled
      ? (t0 > 0
          ? `🚚 Add bundle 2 to unlock FREE shipping + ${t0}% off`
          : '🚚 Add bundle 2 to unlock FREE shipping')
      : '';

    this.stepEls = [...this.querySelectorAll('[data-bb-step]')];
    this.stepCount = this.stepEls.length;
    this.storageKey = `bb:${this.sectionId}`;

    this.state = {
      bundles: [],
      cartIds: [], // song song bundles: id của bundle nếu ĐÃ có trong giỏ (null = chưa) -> chống add trùng
      draft: Array(this.stepCount).fill(null),
      current: 0,
      editingBundle: null, // null = đang dựng draft; số = đang sửa bundles[n]
    };
    this.search = this.stepEls.map(() => null);
    this.selectedBrand = ''; // brand đang chọn, nhớ & carry-over giữa các step
    this.lastOption = ''; // nhớ size khách chọn gần nhất

    // Danh sách brand khách chọn (merchant nhập). Tách theo dòng hoặc dấu phẩy.
    this.brands = [];
    try {
      const bEl = this.querySelector('[data-bb-brands]');
      if (bEl) this.brands = JSON.parse(bEl.textContent).split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    } catch {
      /* no brands */
    }

    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    this.addEventListener('click', (e) => this.#onClick(e), opts);
    this.addEventListener('change', (e) => this.#onChange(e), opts);
    this.addEventListener('input', (e) => this.#onInput(e), opts);
    // Prefetch view bbsearch lần focus ô lọc brand đầu -> Shopify compile sẵn template.
    this.searchWarmed = false;
    this.addEventListener('focusin', (e) => {
      if (e.target.matches && e.target.matches('[data-bb-brandfilter]')) this.#warmSearch(e.target.closest('[data-bb-step]'));
    }, opts);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.#closeGuide();
        this.#closeZoom();
        this.#closePeek();
      } else if (this.zoom && e.key === 'ArrowLeft') {
        this.#zoomStep(-1);
      } else if (this.zoom && e.key === 'ArrowRight') {
        this.#zoomStep(1);
      }
    }, opts);
    // Giỏ đổi từ NƠI KHÁC (vd xóa bundle ở cart drawer) -> builder tự đối chiếu lại, khỏi F5.
    document.addEventListener('cart:update', () => this.#onCartUpdated(), opts);
    this.reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#io = new IntersectionObserver((entries) => this.#onSentinel(entries), { rootMargin: '600px' });
    this.querySelectorAll('[data-bb-sentinel]').forEach((s) => this.#io.observe(s));

    this.#restore();
    // "Add bundle 2" từ cart (?bb_add=1) -> vào THẲNG step 1 của bundle mới, không về màn final.
    this.pendingAdd = new URLSearchParams(window.location.search).get('bb_add') === '1';
    // Express flow: khách bấm "Add bundle 2 to unlock free ship" -> dựng xong là ADD THẲNG vào giỏ,
    // không bắt kéo xuống bấm "Add to cart". Cờ này sống tới khi bundle hoàn tất rồi auto-checkout.
    this.expressAdd = this.pendingAdd;
    if (this.pendingAdd) {
      try { window.history.replaceState({}, '', window.location.pathname); } catch {}
    }
    // Hiển thị ngay theo session (nhanh); rồi ĐỐI CHIẾU với GIỎ để sửa nếu lệch
    // (vd khách đã xóa bundle ở cart drawer -> session vẫn còn cũ).
    if (this.pendingAdd) {
      this.#addAnother();
    } else if (this.state.bundles.length > 0) {
      this.#showResult(false);
    } else {
      this.#stepTo(Math.max(this.#emptySlot(this.state.draft), 0), false);
    }
    const cartCount = this.#cartCountHint();
    if (cartCount === 0) {
      // Giỏ rỗng -> không còn committed; chỉ giữ bundle đang dựng dở. Không cần fetch.
      this.#reconcileBundles([], []);
    } else {
      this.#loadCartBundles().then(({ bundles, cartIds }) => this.#reconcileBundles(bundles, cartIds));
    }

    this.#track('BundleStarted', { bundle_name: this.bundleName, num_steps: this.stepCount });
    this.startedTracked = true;
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#io?.disconnect();
  }

  /* ---------- helpers ---------- */

  #target() {
    return this.state.editingBundle === null ? this.state.draft : this.state.bundles[this.state.editingBundle];
  }
  #emptySlot(arr) {
    return arr.findIndex((s) => !s);
  }
  #isFull(arr) {
    return arr.length > 0 && arr.every(Boolean);
  }
  #activeHandle(stepEl) {
    const tab = stepEl.querySelector('[data-bb-subtab][aria-selected="true"]');
    return (tab && tab.dataset.handle) || stepEl.dataset.collectionHandle || '';
  }
  /**
   * Từ khoá scope cho TAB đang chọn. Vì scope phải khớp TÊN sản phẩm (tên collection
   * không đáng tin), merchant nhập danh sách scope ngăn cách dấu phẩy theo thứ tự tab
   * (vd "short sleeve polo, t-shirt, hawaiian shirt"). Ưu tiên:
   *   1) manual[tabIndex] (thiếu thì lấy phần tử cuối) — khi có nhập tay
   *   2) data-scope của tab (auto từ tên collection) — fallback
   *   3) scope của collection mặc định (single-collection)
   */
  #activeScope(stepEl) {
    const manual = (stepEl.dataset.searchScope || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const tabs = [...stepEl.querySelectorAll('[data-bb-subtab]')];
    if (tabs.length) {
      let i = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      if (i < 0) i = 0;
      if (manual.length) return (manual[i] || manual[manual.length - 1] || '').toLowerCase();
      return (tabs[i].dataset.scope || '').toLowerCase();
    }
    return (manual[0] || stepEl.dataset.collectionScope || '').toLowerCase();
  }

  /** Slug brand giống cách Shopify sinh handle: thường hoá, ký tự đặc biệt -> '-'. */
  #slugifyBrand(b) {
    return String(b || '')
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
  /**
   * Pattern handle collection-theo-brand cho TAB đang chọn (căn theo tab như #activeScope).
   * Merchant nhập "{brand}-short-sleeve-polo, {brand}-t-shirt, ..." theo thứ tự tab.
   * Trả '' nếu không cấu hình.
   */
  #activePattern(stepEl) {
    const list = (stepEl.dataset.brandPattern || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!list.length) return '';
    const tabs = [...stepEl.querySelectorAll('[data-bb-subtab]')];
    if (tabs.length) {
      let i = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      if (i < 0) i = 0;
      return list[i] || list[list.length - 1] || '';
    }
    return list[0] || '';
  }
  /**
   * Dựng handle collection trực tiếp theo brand từ pattern (vd "porsche-short-sleeve-polo").
   * Null nếu chưa cấu hình pattern -> giữ luồng search store cũ.
   */
  #directHandle(stepEl, brand) {
    const pat = this.#activePattern(stepEl);
    const slug = this.#slugifyBrand(brand);
    if (!pat || !slug) return null;
    return pat.includes('{brand}') ? pat.replace(/\{brand\}/g, slug) : `${slug}-${pat}`;
  }

  /* ---------- events ---------- */

  #onClick(e) {
    const tab = e.target.closest('[data-bb-subtab]');
    if (tab) return this.#switchTab(tab);

    const brandBtn = e.target.closest('[data-bb-brandbtn]');
    if (brandBtn) {
      const stepEl = brandBtn.closest('[data-bb-step]');
      this.#track('BrandSelected', {
        brand: brandBtn.dataset.brand,
        step: parseInt(stepEl.dataset.bbStep, 10) + 1,
      });
      return this.#selectBrand(stepEl, brandBtn.dataset.brand, true);
    }

    const brandChange = e.target.closest('[data-bb-brandchange]');
    if (brandChange) return this.#openPicker(brandChange.closest('[data-bb-step]'));

    const pill = e.target.closest('[data-bb-pill]');
    if (pill) return this.#selectPill(pill);

    const zoom = e.target.closest('[data-bb-zoom]');
    // Lưới: dataset nằm trên card; result: ảnh tự mang dataset -> fallback chính nó.
    if (zoom) return this.#openZoom(zoom.closest('[data-bb-card]') || zoom);
    if (e.target.closest('[data-bb-zoom-prev]')) return this.#zoomStep(-1);
    if (e.target.closest('[data-bb-zoom-next]')) return this.#zoomStep(1);
    const zthumb = e.target.closest('[data-bb-zoom-thumb]');
    if (zthumb && this.zoom) {
      this.zoom.index = parseInt(zthumb.dataset.bbZoomThumb, 10) || 0;
      return this.#renderZoom();
    }
    if (e.target.closest('[data-bb-lightbox-close]')) return this.#closeZoom();

    const addBtn = e.target.closest('[data-bb-add]');
    if (addBtn && !addBtn.disabled) return this.#addItem(addBtn.closest('[data-bb-card]'));

    const slot = e.target.closest('[data-bb-slot]');
    if (slot) return this.#stepTo(parseInt(slot.dataset.bbSlot, 10), true);

    const edit = e.target.closest('[data-bb-edit]');
    if (edit) {
      const b = edit.dataset.bbBundle;
      this.state.editingBundle = b != null && b !== '' ? parseInt(b, 10) : null;
      this.#closePeek();
      return this.#stepTo(parseInt(edit.dataset.bbEdit, 10), true);
    }

    const rm = e.target.closest('[data-bb-remove-bundle]');
    if (rm) return this.#removeBundle(parseInt(rm.dataset.bbRemoveBundle, 10));

    if (e.target.closest('[data-bb-add-another]')) return this.#addAnother();
    if (e.target.closest('[data-bb-back]')) return this.#backToBuilder();
    if (e.target.closest('[data-bb-view]')) return this.#openPeek();
    if (e.target.closest('[data-bb-peek-close]')) return this.#closePeek();
    if (e.target.closest('[data-bb-peek-full]')) {
      this.#closePeek();
      return this.#showResult(true);
    }
    if (e.target.closest('[data-bb-checkout]')) return this.#checkout();

    const guide = e.target.closest('[data-bb-sizeguide]');
    if (guide) return this.#openGuide(guide.dataset.bbSizeguide);
    if (e.target.closest('[data-bb-modal-close]')) return this.#closeGuide();
  }

  #onChange(e) {
    const card = e.target.closest('[data-bb-card]');
    if (!card) return;
    if (e.target.matches('[data-bb-variant-select]')) {
      const opt = e.target.options[e.target.selectedIndex];
      if (opt && opt.value) {
        card.dataset.selectedVariant = opt.value;
        this.lastOption = opt.textContent;
      }
      this.#refreshCard(card);
    }
  }

  #onInput(e) {
    const input = e.target.closest('[data-bb-brandfilter]');
    if (!input) return;
    const stepEl = input.closest('[data-bb-step]');
    this.#filterBrandGrid(stepEl, input.value.trim());
  }

  /* ---------- brand autocomplete ---------- */

  /** Điểm khớp: 0 = bắt đầu bằng query; 1 = có TỪ bắt đầu bằng query; 2 = chứa; 3 = không. */
  #matchScore(text, q) {
    const t = (text || '').toLowerCase();
    if (t.startsWith(q)) return 0;
    if (t.split(/[\s/&,-]+/).some((w) => w.startsWith(q))) return 1;
    return t.includes(q) ? 2 : 3;
  }

  /**
   * Tên sản phẩm có ĐÚNG brand đã chọn không? Chỉ giữ sản phẩm của brand đó
   * (Shopify search khớp OR theo scope nên trả về cả brand khác — phải lọc lại).
   * So theo TỪ đầu của brand ở mức word-boundary (vd "Dodge" -> chỉ Dodge,
   * không dính "Duramax"); brand nhiều từ ("Mercedes-Benz") khớp qua từ "mercedes".
   */
  #titleMatchesBrand(title, brand) {
    const first = (brand || '').toLowerCase().split(/[\s/&,-]+/).filter(Boolean)[0] || '';
    if (first.length < 2) return true; // brand quá ngắn -> không lọc để khỏi sót
    return this.#matchScore(title, first) <= 1; // bắt đầu hoặc có từ bắt đầu bằng brand
  }

  /**
   * Mở rộng chữ gõ partial -> tên brand đầy đủ (từ brand list), để gõ "acu" cũng
   * ra Acura (store search của Shopify khớp nguyên từ nên cần tên đầy đủ).
   * Chỉ mở rộng khi là prefix/từ-bắt-đầu của đúng brand; không khớp -> giữ nguyên.
   */
  #resolveBrand(q) {
    if (!this.brands.length || !q) return null;
    const ql = q.toLowerCase();
    const ranked = this.brands
      .map((b) => ({ b, s: this.#matchScore(b, ql) }))
      .filter((x) => x.s <= 1)
      .sort((a, b) => a.s - b.s);
    return ranked.length ? ranked[0].b : null;
  }

  /* ---------- brand selector (bắt buộc) ---------- */

  /** Vào step: auto-áp brand đã chọn (carry-over) hoặc mở picker để bắt chọn. */
  #enterStep(stepEl) {
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    this.#prefetchGuide(stepEl);
    // Collection-mode: không brand -> load thẳng collection của tab đang chọn.
    if (this.collectionMode) {
      // CSS ẩn .bb-grid/tabs/status khi step CHƯA có brand -> collection-mode set cờ này
      // để hiện lưới + tabs (các element brand đã bị {%- unless -%} bỏ render nên vô hại).
      stepEl.dataset.bbHasBrand = '';
      if (!this.search[idx]) this.#loadCollection(stepEl);
      else this.#applyVisibility(stepEl);
      return;
    }
    const stepBrand = stepEl.dataset.brand || '';
    if (stepBrand) {
      stepEl.dataset.bbHasBrand = '';
      if (!this.search[idx]) this.#runSearch(stepEl, stepBrand);
      else this.#applyVisibility(stepEl);
      return;
    }
    if (this.selectedBrand) {
      this.#selectBrand(stepEl, this.selectedBrand, false);
      return;
    }
    this.#openPicker(stepEl);
  }

  /** Collection-mode: load THẲNG collection của tab đang chọn (không brand, không lọc tên). */
  #loadCollection(stepEl) {
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    const handle = this.#activeHandle(stepEl);
    if (!handle) return;
    const grid = stepEl.querySelector('[data-bb-grid]');
    const status = stepEl.querySelector('[data-bb-search-status]');
    // Tái dùng luồng "direct": s.direct = handle collection -> /collections/{handle}?view=bbsearch.
    this.search[idx] = { q: '', term: '', handle, direct: handle, page: 0, pages: 0, more: true, loading: false, seen: new Set() };
    if (grid) grid.querySelectorAll('[data-bb-search-card]').forEach((c) => c.remove());
    this.#clearEmptyState(grid);
    this.#applyVisibility(stepEl);
    if (status) {
      status.classList.remove('bb-hide');
      status.dataset.loading = '';
      status.textContent = this.searchingLabel;
    }
    this.#showSkeletons(grid, 8);
    this.#loadSearch(stepEl);
  }

  /**
   * @param {boolean} explicit  true = khách CHỦ ĐỘNG bấm chọn brand ở step này;
   *   false = kế thừa tự động (carry-over) từ step trước.
   * Khi chọn chủ động: cập nhật brand mặc định + reset các step KHÁC còn kế thừa
   * (chưa tự chọn) để chúng nhận brand MỚI khi vào -> fix bug carry-over cũ, vẫn
   * giữ mix-and-match cho step nào khách đã tự chọn riêng.
   */
  #selectBrand(stepEl, brand, explicit = false) {
    if (!brand) return;
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    if (!this.brandExplicit) this.brandExplicit = new Set();
    this.#warmSearch(stepEl);
    stepEl.dataset.brand = brand;
    stepEl.dataset.bbHasBrand = '';
    this.selectedBrand = brand;
    if (explicit) {
      this.brandExplicit.add(idx);
      this.stepEls.forEach((el, j) => {
        if (j !== idx && !this.brandExplicit.has(j)) this.#resetStepBrand(el);
      });
    }
    const nameEl = stepEl.querySelector('[data-bb-brandbar-name]');
    if (nameEl) nameEl.textContent = brand;
    const filter = stepEl.querySelector('[data-bb-brandfilter]');
    if (filter) filter.value = '';
    this.#filterBrandGrid(stepEl, '');
    this.#persist();
    this.#runSearch(stepEl, brand);
  }

  /** Xoá trạng thái brand của 1 step (không focus) -> lần vào sau sẽ kế thừa brand mới. */
  #resetStepBrand(stepEl) {
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    delete stepEl.dataset.brand;
    stepEl.removeAttribute('data-bb-has-brand');
    this.search[idx] = null;
    const grid = stepEl.querySelector('[data-bb-grid]');
    if (grid) {
      grid.querySelectorAll('[data-bb-search-card]').forEach((c) => c.remove());
      this.#clearSkeletons(grid);
      this.#clearEmptyState(grid);
    }
    const status = stepEl.querySelector('[data-bb-search-status]');
    if (status) {
      status.classList.add('bb-hide');
      delete status.dataset.loading;
    }
    const filter = stepEl.querySelector('[data-bb-brandfilter]');
    if (filter) {
      filter.value = '';
      this.#filterBrandGrid(stepEl, '');
    }
  }

  /** Mở lại lưới chọn brand (đổi brand): xoá cờ explicit của step này + reset + focus. */
  #openPicker(stepEl) {
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    if (this.brandExplicit) this.brandExplicit.delete(idx);
    this.#resetStepBrand(stepEl);
    const filter = stepEl.querySelector('[data-bb-brandfilter]');
    if (filter) filter.focus({ preventScroll: true });
  }

  /** Lọc + xếp hạng nút brand: prefix-match lên trước (gõ "M" -> brand bắt đầu bằng M),
   *  rồi từ-bắt-đầu, rồi chứa; ẩn nút không khớp. Rỗng = khôi phục thứ tự gốc. */
  #filterBrandGrid(stepEl, q) {
    const grid = stepEl.querySelector('[data-bb-brandgrid]');
    if (!grid) return;
    const query = (q || '').toLowerCase();
    let visible = 0;
    const ranked = [...grid.querySelectorAll('[data-bb-brandbtn]')].map((btn) => {
      const s = query ? this.#matchScore(btn.dataset.brand || '', query) : 0;
      const show = !query || s <= 2;
      btn.hidden = !show;
      if (show) visible++;
      return { btn, s, o: parseInt(btn.dataset.order, 10) || 0 };
    });
    // Xếp: hạng khớp tăng dần (0=prefix), cùng hạng giữ thứ tự gốc.
    ranked.sort((a, b) => a.s - b.s || a.o - b.o);
    ranked.forEach((x) => grid.append(x.btn));
    const none = stepEl.querySelector('[data-bb-brand-none]');
    if (none) none.classList.toggle('bb-hide', visible !== 0);
  }

  #onSentinel(entries) {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const stepEl = entry.target.closest('[data-bb-step]');
      if (!stepEl || stepEl.classList.contains('bb-hide')) continue;
      const idx = parseInt(stepEl.dataset.bbStep, 10);
      if (this.search[idx]) this.#loadSearch(stepEl);
    }
  }

  /* ---------- size guide modal ---------- */

  #openGuide(src) {
    const modal = this.querySelector('[data-bb-modal]');
    if (!modal || !src) return;
    const img = modal.querySelector('[data-bb-modal-img]');
    if (img.getAttribute('src') !== src) {
      // Ảnh chưa tải xong -> hiện spinner, ẩn ảnh tới khi onload (đã prefetch thì gần như tức thì).
      modal.classList.add('bb-modal--loading');
      img.classList.remove('bb-modal__img--ready');
      img.onload = () => {
        modal.classList.remove('bb-modal--loading');
        img.classList.add('bb-modal__img--ready');
      };
      img.onerror = () => modal.classList.remove('bb-modal--loading');
      img.src = src;
      if (img.complete && img.naturalWidth) img.onload(); // đã có trong cache
    } else {
      img.classList.add('bb-modal__img--ready');
    }
    modal.classList.remove('bb-hide');
    modal.setAttribute('aria-hidden', 'false');
  }

  /** Tải sẵn ảnh size guide của step (chạy lúc nhàn rỗi) -> mở modal là có ngay, cả mobile. */
  #prefetchGuide(stepEl) {
    const btn = stepEl && stepEl.querySelector('[data-bb-sizeguide]');
    const url = btn && btn.dataset.bbSizeguide;
    if (!url) return;
    if (!this.guidePrefetched) this.guidePrefetched = new Set();
    if (this.guidePrefetched.has(url)) return;
    this.guidePrefetched.add(url);
    const warm = () => {
      const im = new Image();
      if ('fetchPriority' in im) im.fetchPriority = 'low';
      im.decoding = 'async';
      im.src = url;
    };
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 2000 });
    else setTimeout(warm, 300);
  }
  #closeGuide() {
    const modal = this.querySelector('[data-bb-modal]');
    if (!modal) return;
    modal.classList.add('bb-hide');
    modal.setAttribute('aria-hidden', 'true');
  }

  /* ---------- product image lightbox (zoom) ---------- */

  /** Trả URL ảnh ở width yêu cầu — GHI ĐÈ tham số width có sẵn (tránh ?width=400&width=1400). */
  #sizeUrl(u, w) {
    const url = httpsUrl(u);
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.href);
      parsed.searchParams.set('width', String(w));
      return parsed.href;
    } catch {
      return `${url}${url.includes('?') ? '&' : '?'}width=${w}`;
    }
  }

  /** Width ảnh zoom theo viewport (làm tròn 100px để cache CDN tốt) -> mobile nhẹ, desktop nét. */
  #zoomWidth() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const vw = Math.min(window.innerWidth || 800, 1000);
    return Math.max(700, Math.min(1600, Math.round((vw * dpr) / 100) * 100));
  }

  #resultImgWidth() {
    // Ô ảnh result bị giới hạn ~260px (CSS), serve ≥2× để nét mọi DPR.
    return (window.innerWidth || 800) < 700 ? 480 : 600;
  }

  #openZoom(card) {
    const box = this.querySelector('[data-bb-lightbox]');
    if (!box || !card) return;
    const raw = card.dataset.zoomraw || '';
    const first = raw ? this.#sizeUrl(raw, this.#zoomWidth()) : card.dataset.image;
    this.zoom = {
      main: first ? [first] : [],
      thumbs: first ? [first] : [],
      index: 0,
      title: card.dataset.title || '',
      handle: card.dataset.handle || '',
    };
    const cap = box.querySelector('[data-bb-lightbox-caption]');
    if (cap) cap.textContent = this.zoom.title;
    const detail = box.querySelector('[data-bb-lightbox-detail]');
    if (detail) {
      if (this.zoom.handle) {
        detail.href = `/products/${this.zoom.handle}`;
        detail.classList.remove('bb-hide');
      } else {
        detail.classList.add('bb-hide');
      }
    }
    this.#renderZoom();
    box.classList.remove('bb-hide');
    box.setAttribute('aria-hidden', 'false');
    if (this.zoom.handle) this.#loadZoomImages(this.zoom.handle);
  }

  /** Tải TẤT CẢ ảnh của sản phẩm khi zoom (lazy, cache theo handle) -> không phình lưới. */
  async #loadZoomImages(handle) {
    if (!this.zoomCache) this.zoomCache = new Map();
    const build = (imgs) => {
      if (!imgs || !imgs.length) return;
      if (!this.zoom || this.zoom.handle !== handle) return; // đã đóng/đổi sản phẩm
      const main = imgs.map((u) => this.#sizeUrl(u, this.#zoomWidth())).filter(Boolean);
      if (main.length < 2) return; // chỉ 1 ảnh -> giữ nguyên, không cần gallery
      this.zoom.main = main;
      this.zoom.thumbs = imgs.map((u) => this.#sizeUrl(u, 160)).filter(Boolean);
      this.zoom.index = 0;
      this.#renderZoom();
    };
    if (this.zoomCache.has(handle)) return build(this.zoomCache.get(handle));
    try {
      const data = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const imgs = Array.isArray(data.images) ? data.images : [];
      this.zoomCache.set(handle, imgs);
      build(imgs);
    } catch {
      /* giữ ảnh featured */
    }
  }

  #renderZoom() {
    const box = this.querySelector('[data-bb-lightbox]');
    if (!box || !this.zoom) return;
    const { main, thumbs, index, title } = this.zoom;
    const img = box.querySelector('[data-bb-lightbox-img]');
    if (img && main.length) {
      img.src = main[index] || main[0];
      img.alt = title;
    }
    const multi = main.length > 1;
    box.querySelectorAll('[data-bb-zoom-prev],[data-bb-zoom-next]').forEach((b) => b.classList.toggle('bb-hide', !multi));
    const wrap = box.querySelector('[data-bb-lightbox-thumbs]');
    if (wrap) {
      wrap.classList.toggle('bb-hide', !multi);
      if (multi) {
        wrap.textContent = '';
        thumbs.forEach((src, i) => {
          const t = document.createElement('button');
          t.type = 'button';
          t.className = 'bb-lightbox__thumb';
          t.dataset.bbZoomThumb = String(i);
          t.setAttribute('aria-selected', i === index ? 'true' : 'false');
          const ti = document.createElement('img');
          ti.src = src;
          ti.alt = '';
          ti.loading = 'lazy';
          t.append(ti);
          wrap.append(t);
        });
      }
    }
  }

  #zoomStep(delta) {
    if (!this.zoom || this.zoom.main.length < 2) return;
    const n = this.zoom.main.length;
    this.zoom.index = (this.zoom.index + delta + n) % n;
    this.#renderZoom();
  }

  #closeZoom() {
    const box = this.querySelector('[data-bb-lightbox]');
    if (!box) return;
    box.classList.add('bb-hide');
    box.setAttribute('aria-hidden', 'true');
    const img = box.querySelector('[data-bb-lightbox-img]');
    if (img) img.src = '';
    this.zoom = null;
  }

  /* ---------- tabs (multi-collection) ---------- */

  #switchTab(tab) {
    const stepEl = tab.closest('[data-bb-step]');
    if (tab.getAttribute('aria-selected') === 'true') return;
    stepEl.querySelectorAll('[data-bb-subtab]').forEach((t) => t.setAttribute('aria-selected', t === tab ? 'true' : 'false'));
    // Collection-mode: đổi tab = đổi collection -> load thẳng collection mới.
    if (this.collectionMode) return this.#loadCollection(stepEl);
    const brand = stepEl.dataset.brand || '';
    // Đổi tab (collection) -> search lại brand trong collection vừa chọn.
    if (brand) this.#runSearch(stepEl, brand);
  }

  /** Ẩn/hiện card theo (đang search?, tab active). 1 nguồn sự thật. */
  #applyVisibility(stepEl) {
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    const searching = !!this.search[idx];
    const active = this.#activeHandle(stepEl);
    stepEl.querySelectorAll('[data-bb-card]').forEach((card) => {
      let visible;
      if (card.hasAttribute('data-bb-search-card')) visible = searching;
      else visible = !searching && card.dataset.bbHandle === active;
      card.toggleAttribute('data-bb-hidden', !visible);
    });
  }

  /* ---------- card option logic ---------- */

  #variants(card) {
    try {
      return JSON.parse(card.querySelector('[data-bb-variants]').textContent);
    } catch {
      return [];
    }
  }

  /** Auto chọn variant: ưu tiên size khách đã chọn trước đó, không thì variant đầu. */
  #autoSelect(card) {
    if (card.dataset.mode === 'single') {
      const v = this.#variants(card)[0];
      if (v) card.dataset.selectedVariant = String(v[0]);
      this.#refreshCard(card);
      return;
    }
    // Pills hoặc select đều mang option [data-variant-id]/<option value>.
    const pills = [...card.querySelectorAll('[data-bb-pill]')];
    const select = card.querySelector('[data-bb-variant-select]');
    const opts = pills.length
      ? pills.map((p) => ({ id: p.dataset.variantId, label: p.dataset.label, el: p }))
      : [...(select?.options || [])].filter((o) => o.value).map((o) => ({ id: o.value, label: o.textContent, el: o }));
    const match = this.lastOption && opts.find((o) => o.label === this.lastOption);
    const chosen = match || opts[0];
    if (chosen) {
      card.dataset.selectedVariant = chosen.id;
      if (pills.length) pills.forEach((p) => p.setAttribute('aria-pressed', p === chosen.el ? 'true' : 'false'));
      else if (select) select.value = chosen.id;
    }
    this.#refreshCard(card);
  }

  #selectPill(pill) {
    const card = pill.closest('[data-bb-card]');
    if (!card) return;
    card.querySelectorAll('[data-bb-pill]').forEach((p) => p.setAttribute('aria-pressed', p === pill ? 'true' : 'false'));
    card.dataset.selectedVariant = pill.dataset.variantId;
    if (pill.dataset.label) this.lastOption = pill.dataset.label;
    this.#refreshCard(card);
  }

  #resolveVariant(card) {
    const variants = this.#variants(card);
    if (card.dataset.mode === 'single') return variants[0] || null;
    const id = parseInt(card.dataset.selectedVariant, 10);
    return variants.find((v) => v[0] === id) || null;
  }

  #refreshCard(card) {
    const variant = this.#resolveVariant(card);
    const priceEl = card.querySelector('[data-bb-card-price]');
    if (variant && priceEl) priceEl.textContent = money(variant[3]);
    card.querySelector('[data-bb-add]').disabled = !variant;
  }

  /* ---------- bundle state ---------- */

  #addItem(card) {
    const variant = this.#resolveVariant(card);
    if (!variant) return;
    const step = parseInt(card.dataset.step, 10);
    const pill = card.querySelector('[data-bb-pill][aria-pressed="true"]');
    const select = card.querySelector('[data-bb-variant-select]');
    let label = '';
    if (pill) label = pill.dataset.label || '';
    else if (select) label = select.options[select.selectedIndex]?.textContent || '';
    if (label) this.lastOption = label;

    this.#target()[step] = {
      productId: parseInt(card.dataset.productId, 10),
      variantId: variant[0],
      title: card.dataset.title,
      image: card.dataset.image,            // 240px — cho slot/peek
      imageRaw: card.dataset.zoomraw || '', // URL gốc — size to ở màn result
      handle: card.dataset.handle || '',    // -> zoom gallery + link chi tiết ở result
      variantLabel: label,
      priceCents: variant[3],
    };

    this.#track('BundleItemAdded', {
      brand: card.closest('[data-bb-step]')?.dataset.brand || this.selectedBrand || '',
      step: step + 1,
      content_id: String(variant[0]),
      product_id: String(parseInt(card.dataset.productId, 10)),
      value: variant[3] / 100,
      currency: this.currency,
    });

    this.#flyToSlot(card, step);
    this.#pulseSlot(step);

    if (this.state.editingBundle !== null) {
      const idx = this.state.editingBundle;
      // Sửa bundle đã nằm trong giỏ -> gỡ bản cũ khỏi giỏ, đánh dấu cần add lại lúc checkout.
      if (this.state.cartIds[idx]) {
        this.#removeBundleFromCart(this.state.cartIds[idx]);
        this.state.cartIds[idx] = null;
      }
      this.state.editingBundle = null;
      this.#persist();
      return this.#showResult(true);
    }

    this.#persist();
    this.#priceUpdate();

    if (this.#isFull(this.state.draft)) {
      const c = this.#calc(this.state.draft, this.#targetIndex());
      this.#track('BundleCompleted', {
        bundle_name: this.bundleName,
        num_items: this.state.draft.length,
        value: c.pay / 100,
        currency: this.currency,
      });
      this.state.bundles.push(this.state.draft);
      this.state.cartIds.push(null); // bundle mới -> chưa trong giỏ
      this.state.draft = Array(this.stepCount).fill(null);
      this.#persist();
      this.#showResult(true);
      // Express ("Add bundle 2 to unlock free ship"): hiện màn final RỒI add luôn vào giỏ,
      // khách không phải kéo xuống bấm "Add to cart" nữa. Chỉ chạy 1 lần cho lượt express.
      if (this.expressAdd) {
        this.expressAdd = false;
        this.#checkout();
      }
      return;
    }
    this.#stepTo(this.#emptySlot(this.state.draft), true);
  }

  #pulseSlot(step) {
    const slot = this.querySelector(`[data-bb-slot="${step}"]`);
    if (!slot) return;
    slot.classList.remove('bb-bar__slot--pulse');
    void slot.offsetWidth; // restart animation
    slot.classList.add('bb-bar__slot--pulse');
  }

  /** Ảnh sản phẩm "bay" từ card vào ô slot tương ứng ở thanh đáy. */
  #flyToSlot(card, step) {
    if (this.reduceMotion) return;
    const imgEl = card.querySelector('.bb-card__media img');
    const slot = this.querySelector(`[data-bb-slot="${step}"]`);
    if (!imgEl || !slot) return;
    const from = imgEl.getBoundingClientRect();
    const to = slot.getBoundingClientRect();
    if (!from.width || !to.width) return;

    const fly = document.createElement('img');
    fly.src = imgEl.currentSrc || imgEl.src;
    fly.setAttribute('aria-hidden', 'true');
    Object.assign(fly.style, {
      position: 'fixed',
      left: `${from.left}px`,
      top: `${from.top}px`,
      width: `${from.width}px`,
      height: `${from.height}px`,
      objectFit: 'cover',
      borderRadius: '10px',
      zIndex: '2000',
      pointerEvents: 'none',
      transition: 'transform 0.55s cubic-bezier(0.5, -0.2, 0.3, 1), opacity 0.55s ease',
      willChange: 'transform, opacity',
    });
    document.body.append(fly);

    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    const scale = Math.max(0.18, Math.min(0.5, to.width / from.width));
    requestAnimationFrame(() => {
      fly.style.transform = `translate(${dx}px, ${dy}px) scale(${scale})`;
      fly.style.opacity = '0.35';
    });
    const cleanup = () => fly.remove();
    fly.addEventListener('transitionend', cleanup, { once: true });
    setTimeout(cleanup, 800); // phòng transitionend không bắn
  }

  /** Làm ấm view bbsearch (Shopify compile template) -> search thật ra nhanh hơn. */
  #warmSearch(stepEl) {
    if (this.searchWarmed) return;
    this.searchWarmed = true;
    const scope = this.#activeScope(stepEl) || 'shirt';
    fetch(`/search?q=${encodeURIComponent(scope)}&type=product&view=bbsearch&page=1`).catch(() => {});
  }

  #stepTo(index, focus) {
    if (index < 0 || index >= this.stepCount) return;
    this.state.current = index;
    this.classList.remove('bb-on-result');
    this.querySelector('[data-bb-result]').classList.add('bb-hide');
    this.stepEls.forEach((el, i) => el.classList.toggle('bb-hide', i !== index));
    this.#barUpdate();
    this.#hideError();

    const stepEl = this.stepEls[index];

    // Vào step: auto-áp brand đã chọn (carry-over) hoặc mở picker bắt chọn.
    this.#enterStep(stepEl);

    if (focus) {
      const title = stepEl.querySelector('.bb-step__title');
      title?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      title?.focus({ preventScroll: true });
    }
  }

  #addAnother() {
    this.state.editingBundle = null;
    const start = this.#emptySlot(this.state.draft);
    this.#stepTo(start === -1 ? 0 : start, true);
  }

  #backToBuilder() {
    this.state.editingBundle = null;
    const start = this.#emptySlot(this.state.draft);
    this.#stepTo(start === -1 ? 0 : start, true);
  }

  #removeBundle(b) {
    const cartId = this.state.cartIds[b];
    this.state.bundles.splice(b, 1);
    this.state.cartIds.splice(b, 1);
    this.#persist();
    if (cartId) this.#removeBundleFromCart(cartId); // bundle đã trong giỏ -> gỡ luôn khỏi giỏ
    if (this.state.bundles.length === 0) {
      const start = this.#emptySlot(this.state.draft);
      this.#stepTo(start === -1 ? 0 : start, true);
    } else {
      this.#showResult(false);
    }
  }

  /** Gỡ tất cả line item cùng _bundle_id ra khỏi giỏ (set qty 0). */
  async #removeBundleFromCart(cartId) {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const updates = {};
      (cart.items || []).forEach((it) => {
        if (it.properties && it.properties._bundle_id === cartId) updates[it.key] = 0;
      });
      if (Object.keys(updates).length === 0) return;
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      document.dispatchEvent(new CartAddEvent({}, this.sectionId, { source: 'bundle-builder-remove', itemCount: 0 }));
    } catch {
      /* non-critical: giỏ sẽ đồng bộ lại lần tải sau */
    }
  }

  /**
   * Đối chiếu state với GIỎ (nguồn chuẩn cho bundle đã checkout):
   * - committed bundle = đúng những gì CÒN trong giỏ (xóa ở cart -> tự biến mất ở builder).
   * - giữ lại bundle đang dựng dở (cartId null, chưa vào giỏ).
   */
  #reconcileBundles(cartBundles, cartIds) {
    if (this.state.draft.some(Boolean) || this.state.editingBundle !== null) return; // đừng phá thao tác đang dở
    const pendingAdd = this.pendingAdd; // đến từ "Add bundle 2" -> đang ở step 1, đừng nhảy về final
    this.pendingAdd = false;
    const pending = [];
    this.state.bundles.forEach((bn, i) => {
      if (!this.state.cartIds[i]) pending.push(bn);
    });
    const newBundles = cartBundles.concat(pending);
    const newCartIds = cartIds.concat(pending.map(() => null));
    const unchanged =
      newBundles.length === this.state.bundles.length &&
      this.state.cartIds.every((id, i) => id === newCartIds[i]);
    if (unchanged) return; // khớp rồi -> không re-render thừa
    this.state.bundles = newBundles;
    this.state.cartIds = newCartIds;
    this.#persist();
    if (pendingAdd) return; // giữ ở step 1 của bundle mới
    if (this.state.bundles.length > 0) this.#showResult(false);
    else this.#stepTo(Math.max(this.#emptySlot(this.state.draft), 0), false);
  }

  /** Giỏ thay đổi từ ngoài builder (cart drawer/page) -> đối chiếu lại để màn final khớp giỏ (khỏi F5). */
  #onCartUpdated() {
    const result = this.querySelector('[data-bb-result]');
    const onResult = result && !result.classList.contains('bb-hide');
    if (!onResult) return; // chỉ cần đồng bộ khi đang hiện màn final
    if (this.state.draft.some(Boolean) || this.state.editingBundle !== null) return;
    const count = this.#cartCountHint();
    if (count === 0) this.#reconcileBundles([], []);
    else this.#loadCartBundles().then(({ bundles, cartIds }) => this.#reconcileBundles(bundles, cartIds));
  }

  /** Đọc nhanh số lượng giỏ từ DOM (cart bubble). Trả null nếu không chắc -> vẫn fetch để an toàn. */
  #cartCountHint() {
    const el = document.querySelector('.cart-bubble__text-count, [data-cart-count], .cart-count, .cart-count-bubble');
    if (!el) return null;
    const n = parseInt((el.textContent || '').replace(/[^\d]/g, ''), 10);
    return Number.isFinite(n) ? n : null;
  }

  /** Đọc giỏ -> dựng lại các bundle ĐỦ món của builder này (để hiện màn final khi quay lại). */
  async #loadCartBundles() {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const groups = new Map();
      (cart.items || []).forEach((it) => {
        const p = it.properties || {};
        if (p._bundle !== this.bundleName || !p._bundle_id) return; // chỉ bundle của builder này
        const slot = parseInt(p._bundle_slot || '0', 10) || 0;
        if (!groups.has(p._bundle_id)) groups.set(p._bundle_id, []);
        const rawImg = (it.featured_image && it.featured_image.url) || it.image || '';
        groups.get(p._bundle_id)[slot] = {
          productId: it.product_id,
          variantId: it.variant_id,
          title: it.product_title || it.title || '',
          image: this.#sizeUrl(rawImg, 240),
          imageRaw: rawImg,
          handle: it.handle || '',
          variantLabel: it.variant_title && it.variant_title !== 'Default Title' ? it.variant_title : '',
          priceCents: it.original_price != null ? it.original_price : it.price,
        };
      });
      const bundles = [];
      const cartIds = [];
      // Sort theo _bundle_id (khớp cách function xếp hạng) -> index = hạng.
      [...groups.keys()].sort().forEach((id) => {
        const arr = groups.get(id);
        const norm = Array(this.stepCount).fill(null);
        for (let i = 0; i < this.stepCount; i++) norm[i] = arr[i] || null;
        if (norm.every(Boolean)) {
          bundles.push(norm);
          cartIds.push(id);
        }
      });
      return { bundles, cartIds };
    } catch {
      return { bundles: [], cartIds: [] };
    }
  }

  /* ---------- pricing math ---------- */

  /** % giảm THÊM cho bundle ở vị trí `index` (0-based). Bundle 1 (0) = 0%. */
  #tierPct(index) {
    if (!Number.isInteger(index) || index < 1) return 0;
    const tiers = this.extraTiers;
    if (!tiers || !tiers.length) return 0;
    return tiers[Math.min(index - 1, tiers.length - 1)];
  }

  /** Vị trí mà target hiện tại chiếm trong bundles -> quyết định tier. */
  #targetIndex() {
    return this.state.editingBundle != null ? this.state.editingBundle : this.state.bundles.length;
  }

  /**
   * {total, pay, savings, basePay, extraPct, extraSavings} cho 1 mảng selections (cents).
   * `index` = vị trí bundle (0-based) -> áp ưu đãi "buy more save more" cho bundle 2+.
   */
  #calc(sels, index = 0) {
    const total = sels.reduce((s, x) => s + (x ? x.priceCents : 0), 0);
    let pay;
    if (this.pricingType === 'bxgy') {
      const free = this.freeSlots.reduce((s, i) => s + (sels[i] ? sels[i].priceCents : 0), 0);
      pay = total - free;
    } else {
      pay = this.bundlePriceCents;
    }
    const basePay = Math.max(0, pay);
    // "Buy more, save more" áp trên phần PHẢI TRẢ (bxgy: đã trừ quà free; fixed: giá cố định).
    const extraPct = this.#tierPct(index);
    // Làm tròn GIỐNG function: target = round(base*(1-pct/100)) -> khớp giá charge, không lệch cent.
    pay = extraPct > 0 ? Math.max(0, Math.round((basePay * (100 - extraPct)) / 100)) : basePay;
    const extraSavings = basePay - pay;
    const savings = Math.max(0, total - pay);
    return { total, pay, savings, basePay, extraPct, extraSavings };
  }

  /** "You save $X (Y%)" — kèm phần trăm tiết kiệm. */
  #savingsText(savings, total) {
    const pct = total > 0 ? Math.round((savings / total) * 100) : 0;
    return `${this.savingsLabel} ${money(savings)}${pct > 0 ? ` (${pct}%)` : ''}`;
  }

  /**
   * Bắn event phễu cho Meta Pixel (+ dataLayer cho GTM nếu có). Non-blocking,
   * fail-safe: không có fbq/dataLayer thì bỏ qua, không bao giờ làm vỡ luồng.
   * standard=true -> 'track' (event chuẩn FB như AddToCart); false -> 'trackCustom'.
   */
  #track(name, data = {}, standard = false) {
    try {
      // 1) Shopify Customer Events bridge (store dùng web pixel sandbox) -> tên snake_case.
      const snake = `bb_${name.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase()}`;
      const pub = window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish;
      if (typeof pub === 'function') window.Shopify.analytics.publish(snake, data);

      // 2) Fallback: Meta Pixel trực tiếp (nếu fbq được nạp thẳng vào theme).
      if (typeof window.fbq === 'function') {
        window.fbq(standard ? 'track' : 'trackCustom', name, data);
      }

      // 3) Fallback: GTM dataLayer.
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: snake, ...data });
      }
    } catch {
      /* analytics non-critical */
    }
  }

  /* ---------- sticky bar ---------- */

  #barUpdate() {
    const onResult = !this.querySelector('[data-bb-result]').classList.contains('bb-hide');
    const target = this.#target();
    this.querySelectorAll('[data-bb-slot]').forEach((slot, i) => {
      const sel = target[i];
      let s = '';
      if (sel) s = 'filled';
      else if (!onResult && i === this.state.current) s = 'active';
      slot.dataset.state = s;
      const thumb = slot.querySelector('[data-bb-slot-thumb]');
      if (thumb) thumb.style.backgroundImage = sel && sel.image ? `url("${sel.image}")` : '';
    });

    this.querySelector('[data-bb-bar]')?.classList.toggle('bb-hide', onResult);
    const hasAny = target.some(Boolean);
    this.querySelector('[data-bb-view]')?.classList.toggle('bb-hide', !(hasAny && !onResult));

    // Nhắc tiến độ.
    const nudge = this.querySelector('[data-bb-nudge]');
    if (nudge) {
      const left = target.filter((x) => !x).length;
      nudge.textContent = left > 0 ? `${left} more item${left === 1 ? '' : 's'} to go` : '';
      nudge.classList.toggle('bb-hide', left === 0);
    }
    this.#priceUpdate();
  }

  #priceUpdate() {
    const { total, pay, savings } = this.#calc(this.#target(), this.#targetIndex());
    const orig = this.querySelector('[data-bb-bar-original]');
    const bundleEl = this.querySelector('[data-bb-bar-bundle]');
    const saveEl = this.querySelector('[data-bb-bar-savings]');
    if (orig) this.#animateMoney(orig, total);
    if (bundleEl) this.#animateMoney(bundleEl, pay);
    if (saveEl) {
      saveEl.textContent = savings > 0 ? this.#savingsText(savings, total) : '';
      saveEl.classList.toggle('bb-hide', savings <= 0);
    }
  }

  /** Tween số tiền hiển thị từ giá trị cũ -> mới (count-up). */
  #animateMoney(el, toCents) {
    const fromCents = parseInt(el.dataset.cents, 10);
    el.dataset.cents = String(toCents);
    if (this.reduceMotion || !Number.isInteger(fromCents) || fromCents === toCents) {
      el.textContent = money(toCents);
      return;
    }
    if (el._raf) cancelAnimationFrame(el._raf);
    const dur = 380;
    let start = null;
    const tick = (ts) => {
      if (start === null) start = ts;
      const p = Math.min(1, (ts - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = money(Math.round(fromCents + (toCents - fromCents) * eased));
      if (p < 1) el._raf = requestAnimationFrame(tick);
    };
    el._raf = requestAnimationFrame(tick);
  }

  /* ---------- result screen ---------- */

  #showResult(focus) {
    this.stepEls.forEach((el) => el.classList.add('bb-hide'));
    const result = this.querySelector('[data-bb-result]');
    result.classList.remove('bb-hide');
    this.classList.add('bb-on-result');

    const wrap = this.querySelector('[data-bb-result-bundles]');
    wrap.textContent = '';
    let grandTotal = 0;
    let grandPay = 0;
    let grandExtra = 0;
    this.state.bundles.forEach((bundle, b) => {
      const { total, pay, savings, extraPct, extraSavings } = this.#calc(bundle, b);
      grandTotal += total;
      grandPay += pay;
      grandExtra += extraSavings;
      wrap.append(this.#renderBundleCard(bundle, b, total, pay, savings, extraPct, extraSavings));
    });

    // Tổng hợp + nút.
    const sumSave = Math.max(0, grandTotal - grandPay);
    const totalEl = this.querySelector('[data-bb-grand-total]');
    const payEl = this.querySelector('[data-bb-grand-pay]');
    const saveEl = this.querySelector('[data-bb-grand-savings]');
    if (totalEl) totalEl.textContent = money(grandTotal);
    if (payEl) payEl.textContent = money(grandPay);
    if (saveEl) {
      saveEl.textContent = sumSave > 0 ? this.#savingsText(sumSave, grandTotal) : '';
      saveEl.classList.toggle('bb-hide', sumSave <= 0);
    }

    // Nút thêm bundle: ghi rõ bundle kế tiếp + ưu đãi thêm ngay trên nút.
    const draftStarted = this.state.draft.some(Boolean);
    const nextIdx = this.state.bundles.length; // bundle kế tiếp sẽ chiếm index này
    const nextPct = this.#tierPct(nextIdx);
    // Free shipping unlock: dựa trên TỔNG phải trả (grandPay) >= ngưỡng.
    const fsActive = this.fsEnabled && this.fsThreshold > 0;
    const fsUnlocked = fsActive && grandPay >= this.fsThreshold;
    const fsUnlocking = fsActive && !fsUnlocked && this.state.bundles.length > 0; // thêm 1 bundle nữa -> mở free-ship
    const addAnother = this.querySelector('[data-bb-add-another]');
    if (addAnother) {
      const lbl = addAnother.querySelector('[data-bb-add-another-label]') || addAnother;
      // Nhãn 2 dòng: hành động (main) + lợi ích (sub) -> gọn, không nhồi nhét.
      const setLbl = (main, sub) => {
        lbl.innerHTML =
          `<span class="bb-addlabel__main">${this.#esc(main)}</span>` +
          (sub ? `<span class="bb-addlabel__sub">${this.#esc(sub)}</span>` : '');
      };
      if (draftStarted) {
        setLbl(addAnother.dataset.continueLabel || 'CONTINUE YOUR BUNDLE', '');
      } else if (fsUnlocking && nextPct > 0) {
        setLbl(`Add bundle ${nextIdx + 1}`, `Unlock free shipping + extra ${nextPct}% off`);
      } else if (fsUnlocking) {
        setLbl(`Add bundle ${nextIdx + 1}`, 'Unlock free shipping');
      } else if (nextPct > 0) {
        setLbl(`Add bundle ${nextIdx + 1}`, `Save extra ${nextPct}%`);
      } else {
        setLbl(addAnother.dataset.addLabel || 'ADD ANOTHER BUNDLE', '');
      }
    }

    // "Buy more, save more" + BẢNG BẬC chi tiết (Bundle 2: −10%, Bundle 3: −15%, …) cho khách rõ.
    const bxmsTag = this.querySelector('[data-bb-bxms-hint]');
    if (bxmsTag) {
      const tiers = this.extraTiers || [];
      const show = tiers.length > 0 && !draftStarted;
      if (show) {
        const tierHtml = tiers
          .map((pct, i) => {
            const n = i + 2; // bundle thứ mấy (tier[0] = bundle 2)
            const cap = i === tiers.length - 1 ? '+' : '';
            return `<span class="bb-bxms-note__tier">Bundle ${n}${cap}: <b>−${pct}%</b></span>`;
          })
          .join('');
        bxmsTag.innerHTML =
          `<span class="bb-bxms-note__h">${this.#esc(this.bxmsLabel)}</span>` +
          `<span class="bb-bxms-note__tiers">${tierHtml}</span>`;
      } else {
        bxmsTag.innerHTML = '';
      }
      bxmsTag.classList.toggle('bb-hide', !show);
    }

    // Free shipping unlock nudge (locked: nhử thêm bundle; unlocked: chúc mừng).
    const fsEl = this.querySelector('[data-bb-freeship]');
    if (fsEl) {
      if (fsActive && this.state.bundles.length > 0) {
        fsEl.textContent = fsUnlocked ? this.fsUnlocked : this.fsLocked;
        fsEl.classList.toggle('bb-freeship--unlocked', fsUnlocked);
        fsEl.classList.toggle('bb-freeship--locked', !fsUnlocked);
        fsEl.classList.remove('bb-hide');
      } else {
        fsEl.classList.add('bb-hide');
      }
    }

    const checkoutBtn = this.querySelector('[data-bb-checkout]');
    if (checkoutBtn) checkoutBtn.disabled = this.state.bundles.length === 0;

    this.#barUpdate();
    if (focus) {
      const heading = result.querySelector('.bb-result__heading');
      heading?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      heading?.focus({ preventScroll: true });
    }
  }

  #renderBundleCard(bundle, b, total, pay, savings, extraPct = 0, extraSavings = 0) {
    const card = document.createElement('div');
    card.className = 'bb-result__bundle-card';

    const head = document.createElement('div');
    head.className = 'bb-result__bundle-head';
    const label = document.createElement('span');
    label.className = 'bb-result__bundle-label';
    label.textContent = `${this.bundleName} BUNDLE ${b + 1}`;
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'bb-result__bundle-remove';
    remove.dataset.bbRemoveBundle = String(b);
    remove.setAttribute('aria-label', `Remove bundle ${b + 1}`);
    remove.textContent = '×';
    head.append(label, remove);

    const items = document.createElement('div');
    items.className = 'bb-result__items';
    bundle.forEach((sel, i) => {
      if (!sel) return;
      const item = document.createElement('div');
      item.className = 'bb-result-item';

      const img = document.createElement('img');
      img.className = 'bb-result-item__img';
      img.src = (sel.imageRaw ? this.#sizeUrl(sel.imageRaw, this.#resultImgWidth()) : '') || sel.image;
      img.alt = sel.title;
      img.loading = 'lazy';
      // Bấm ảnh -> phóng to (tái dùng lightbox của lưới).
      img.dataset.bbZoom = '';
      img.dataset.zoomraw = sel.imageRaw || '';
      img.dataset.image = sel.image || '';
      img.dataset.title = sel.title || '';
      img.dataset.handle = sel.handle || '';

      const zoomHint = document.createElement('span');
      zoomHint.className = 'bb-result-item__zoomhint';
      zoomHint.setAttribute('aria-hidden', 'true');
      zoomHint.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/><path d="M11 8v6M8 11h6"/></svg>';

      const isFree = this.pricingType === 'bxgy' && this.freeSlots.includes(i);
      if (isFree) {
        const badge = document.createElement('span');
        badge.className = 'bb-result-item__free';
        badge.textContent = 'FREE';
        item.append(badge);
      }

      const meta = document.createElement('div');
      meta.className = 'bb-result-item__meta';
      const name = document.createElement('strong');
      name.textContent = sel.title;
      const priceTxt = isFree ? 'FREE' : money(sel.priceCents);
      const detail = sel.variantLabel ? `${sel.variantLabel} — ${priceTxt}` : priceTxt;
      meta.append(name, document.createTextNode(detail));

      const editBtn = document.createElement('button');
      editBtn.type = 'button';
      editBtn.className = 'bb-result-item__edit';
      editBtn.dataset.bbBundle = String(b);
      editBtn.dataset.bbEdit = String(i);
      editBtn.setAttribute('aria-label', `Edit item ${i + 1} of bundle ${b + 1}`);
      editBtn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>';

      item.append(img, zoomHint, editBtn, meta);
      items.append(item);
    });

    const prices = document.createElement('div');
    prices.className = 'bb-result__bundle-prices';
    prices.innerHTML =
      `<s class="bb-result__original">${money(total)}</s>` +
      `<span class="bb-result__bundle">${money(pay)}</span>` +
      (savings > 0 ? `<span class="bb-result__save">${this.#savingsText(savings, total)}</span>` : '');

    card.append(head, items, prices);

    // "Buy more, save more" — ưu đãi thêm cho bundle 2+ (ghi rõ lý do).
    if (extraPct > 0 && extraSavings > 0) {
      const note = document.createElement('div');
      note.className = 'bb-result__bxms';
      const badge = document.createElement('span');
      badge.className = 'bb-result__bxms-badge';
      badge.textContent = `−${extraPct}%`;
      const txt = document.createElement('span');
      txt.textContent = `${this.bxmsLabel} · extra ${money(extraSavings)} off`;
      note.append(badge, txt);
      card.append(note);
    }
    return card;
  }

  /* ---------- peek (xem nhanh bundle đang dựng) ---------- */

  #openPeek() {
    const peek = this.querySelector('[data-bb-peek]');
    if (!peek) return;
    this.#renderPeek();
    peek.classList.remove('bb-hide');
    peek.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => peek.classList.add('bb-peek--open'));
  }

  #closePeek() {
    const peek = this.querySelector('[data-bb-peek]');
    if (!peek || peek.classList.contains('bb-hide')) return;
    peek.classList.remove('bb-peek--open');
    peek.setAttribute('aria-hidden', 'true');
    const hide = () => peek.classList.add('bb-hide');
    if (this.reduceMotion) hide();
    else setTimeout(hide, 260);
  }

  #renderPeek() {
    const target = this.#target();
    const wrap = this.querySelector('[data-bb-peek-items]');
    if (wrap) {
      wrap.textContent = '';
      this.stepEls.forEach((stepEl, i) => {
        const sel = target[i];
        const label = stepEl.querySelector('.bb-step__title')?.lastChild?.textContent?.trim() || `Step ${i + 1}`;
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'bb-peek__item' + (sel ? '' : ' bb-peek__item--empty');
        row.dataset.bbEdit = String(i); // bấm = sửa slot này (draft)
        if (sel) {
          row.innerHTML =
            `<span class="bb-peek__thumb" style="background-image:url('${sel.image}')"></span>` +
            `<span class="bb-peek__meta"><strong>${this.#esc(sel.title)}</strong>` +
            `<span>${sel.variantLabel ? this.#esc(sel.variantLabel) + ' — ' : ''}${money(sel.priceCents)}</span></span>` +
            `<span class="bb-peek__edit" aria-hidden="true">Change</span>`;
        } else {
          row.innerHTML =
            `<span class="bb-peek__thumb bb-peek__thumb--empty" aria-hidden="true">${i + 1}</span>` +
            `<span class="bb-peek__meta"><strong>${this.#esc(label)}</strong><span>Tap to choose</span></span>` +
            `<span class="bb-peek__edit" aria-hidden="true">+ Add</span>`;
        }
        wrap.append(row);
      });
    }
    const { total, pay, savings } = this.#calc(target, this.#targetIndex());
    const set = (sel, txt) => { const el = this.querySelector(sel); if (el) el.textContent = txt; };
    set('[data-bb-peek-total]', money(total));
    set('[data-bb-peek-pay]', money(pay));
    const saveEl = this.querySelector('[data-bb-peek-savings]');
    if (saveEl) {
      saveEl.textContent = savings > 0 ? this.#savingsText(savings, total) : '';
      saveEl.classList.toggle('bb-hide', savings <= 0);
    }
  }

  #esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  /* ---------- data loading (browse + search) ---------- */

  async #fetchView(url) {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async #runSearch(stepEl, query) {
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    const grid = stepEl.querySelector('[data-bb-grid]');
    const status = stepEl.querySelector('[data-bb-search-status]');
    const loadingEl = stepEl.querySelector('[data-bb-loading]');

    if (!query) {
      this.search[idx] = null;
      this.#clearSkeletons(grid);
      this.#clearEmptyState(grid);
      grid.querySelectorAll('[data-bb-search-card]').forEach((c) => c.remove());
      status.classList.add('bb-hide');
      delete status.dataset.loading;
      loadingEl.classList.add('bb-hide');
      return;
    }

    // Search store đã-index theo "brand + từ loại (scope)" cho NHANH, rồi LỌC về
    // đúng collection của step (membership) -> không phụ thuộc kích cỡ collection.
    // Mở rộng partial -> brand đầy đủ (gõ "acu" => search "Acura").
    const scope = this.#activeScope(stepEl);
    const effective = this.#resolveBrand(query) || query;
    this.search[idx] = {
      q: effective.toLowerCase(),
      term: scope ? `${effective} ${scope}` : effective,
      handle: this.#activeHandle(stepEl),
      direct: this.#directHandle(stepEl, effective),
      page: 0,
      pages: 0,
      more: true,
      loading: false,
      seen: new Set(),
    };
    grid.querySelectorAll('[data-bb-search-card]').forEach((c) => c.remove());
    this.#clearEmptyState(grid);
    this.#applyVisibility(stepEl); // ẩn browse
    status.classList.remove('bb-hide');
    status.dataset.loading = '';
    status.textContent = this.searchingLabel;
    this.#showSkeletons(grid, 8);
    this.#loadSearch(stepEl);
  }

  #showSkeletons(grid, n) {
    for (let i = 0; i < n; i++) {
      const sk = document.createElement('div');
      sk.className = 'bb-skeleton';
      sk.dataset.bbSkeleton = '';
      sk.innerHTML = '<div class="bb-skeleton__media"></div><div class="bb-skeleton__line"></div><div class="bb-skeleton__line"></div>';
      grid.append(sk);
    }
  }
  #clearSkeletons(grid) {
    grid.querySelectorAll('[data-bb-skeleton]').forEach((s) => s.remove());
  }

  /** Khi không có sản phẩm: collection-mode -> báo trống chung; brand-mode -> mời đổi brand. */
  #renderEmptyState(grid) {
    grid.querySelectorAll('[data-bb-empty]').forEach((e) => e.remove());
    const box = document.createElement('div');
    box.className = 'bb-empty';
    box.dataset.bbEmpty = '';
    if (this.collectionMode) {
      box.innerHTML = '<p class="bb-empty__title">No products available here.</p>';
    } else {
      const brand = this.selectedBrand ? this.#esc(this.selectedBrand) : 'this brand';
      box.innerHTML =
        `<p class="bb-empty__title">No ${brand} items in this category.</p>` +
        '<p class="bb-empty__hint">Pick another brand to continue.</p>' +
        '<div class="bb-empty__chips"><button type="button" class="bb-chip" data-bb-brandchange>Change brand</button></div>';
    }
    grid.append(box);
  }
  #clearEmptyState(grid) {
    grid.querySelectorAll('[data-bb-empty]').forEach((e) => e.remove());
  }

  async #loadSearch(stepEl) {
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    const s = this.search[idx];
    if (!s || s.loading || !s.more) return;
    s.loading = true;
    const grid = stepEl.querySelector('[data-bb-grid]');
    const status = stepEl.querySelector('[data-bb-search-status]');
    const stale = () => this.search[idx] !== s;
    status.dataset.loading = '';

    try {
      let added = 0;
      // s.direct: trỏ THẲNG collection-theo-brand (1 request, đúng tập, không lọc lại).
      //   -> nhanh hơn nhiều store-wide search. Hỏng/rỗng thì TỰ fallback về search cũ.
      // Ngược lại: store search (đã index, 250/trang) theo brand+scope rồi LỌC về
      //   collection của step (membership). Lặp tới khi có >=1 match hoặc hết kết quả.
      while (s.more && added === 0 && !stale()) {
        const url = s.direct
          ? `/collections/${encodeURIComponent(s.direct)}?view=bbsearch&page=${s.page + 1}`
          : `/search?q=${encodeURIComponent(s.term)}&type=product&view=bbsearch&sort_by=best-selling&page=${s.page + 1}`;
        let data;
        try {
          data = await this.#fetchView(url);
        } catch (err) {
          // Collection direct không tồn tại (404)/lỗi mạng -> quay về search store cũ.
          // (collection-mode: term rỗng nên KHÔNG fallback search store — để lỗi nổi lên.)
          if (s.direct && !this.collectionMode) {
            s.direct = null;
            s.page = 0;
            s.more = true;
            s.seen.clear();
            s.loading = false;
            return this.#loadSearch(stepEl);
          }
          throw err;
        }
        if (stale()) return;
        s.page += 1;
        s.pages += 1;
        // Bỏ qua sản phẩm thiếu ảnh -> tránh card trống xấu UX.
        // Direct: collection đã đúng tập brand -> không cần membership/title-match/sort.
        const batch = s.direct
          ? (data.results || []).filter((r) => !s.seen.has(String(r.id)) && r.image).map((r) => ({ r }))
          : (data.results || [])
              .filter(
                (r) =>
                  !s.seen.has(String(r.id)) &&
                  r.image &&
                  (r.collections || []).includes(s.handle) &&
                  this.#titleMatchesBrand(r.title, s.q)
              )
              .map((r) => ({ r, sc: this.#matchScore(r.title, s.q) }))
              .sort((a, b) => a.sc - b.sc); // brand prefix trước
        if (batch.length) {
          this.#clearSkeletons(grid);
          for (const { r } of batch) {
            s.seen.add(String(r.id));
            const cardEl = this.#buildCard(r, idx, null);
            cardEl.dataset.bbSearchCard = '';
            grid.append(cardEl);
            this.#autoSelect(cardEl);
          }
          added += batch.length;
        }
        s.more = Boolean(data.more);
      }

      // Direct tồn tại nhưng rỗng (0 sản phẩm) -> fallback search cho chắc.
      if (s.direct && added === 0 && grid.querySelectorAll('[data-bb-search-card]').length === 0) {
        s.direct = null;
        s.page = 0;
        s.more = true;
        s.seen.clear();
        s.loading = false;
        return this.#loadSearch(stepEl);
      }

      const count = grid.querySelectorAll('[data-bb-search-card]').length;
      if (count === 0) {
        this.#clearSkeletons(grid);
        if (s.more) {
          status.textContent = this.searchingLabel;
        } else {
          status.textContent = 'No matches in this collection.';
          this.#renderEmptyState(grid);
        }
      } else {
        status.textContent = `${count}${s.more ? '+' : ''} result${count === 1 ? '' : 's'}`;
      }
    } catch {
      /* thử lại lần sau */
    } finally {
      if (!stale()) {
        s.loading = false;
        delete status.dataset.loading;
        if (grid.querySelectorAll('[data-bb-search-card]').length > 0) this.#clearSkeletons(grid);
      }
    }
  }

  #buildCard(product, stepIdx, handle) {
    const variants = (product.variants || []).filter((v) => v[4]).map((v) => [v[0], v[1], v[2], v[3]]);
    const mode = variants.length <= 1 ? 'single' : 'variant-list';
    const img = httpsUrl(product.image);
    const sized = (w) => (img ? this.#sizeUrl(product.image, w) : '');

    const card = document.createElement('div');
    card.className = 'bb-card';
    card.dataset.bbCard = '';
    card.dataset.mode = mode;
    card.dataset.productId = String(product.id);
    card.dataset.title = product.title;
    card.dataset.handle = product.handle || '';
    card.dataset.image = sized(240) || img;
    card.dataset.zoomraw = product.image || ''; // URL gốc -> sẽ size theo màn lúc mở zoom
    card.dataset.step = String(stepIdx);
    if (handle != null) card.dataset.bbHandle = handle;

    const media = document.createElement('div');
    media.className = 'bb-card__media';
    media.dataset.bbZoom = ''; // bấm ảnh -> phóng to
    if (img) {
      const im = document.createElement('img');
      im.src = sized(400) || img;
      if (img) im.srcset = `${sized(200)} 200w, ${sized(300)} 300w, ${sized(400)} 400w, ${sized(600)} 600w`;
      im.sizes = '(max-width: 749px) 45vw, 220px';
      im.width = 400;
      im.height = 400;
      im.alt = product.title;
      im.loading = 'lazy';
      im.decoding = 'async';
      media.append(im);
    }
    const view = document.createElement('button');
    view.type = 'button';
    view.className = 'bb-card__view';
    view.dataset.bbZoom = '';
    view.setAttribute('aria-label', `Zoom ${product.title}`);
    view.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>';
    media.append(view);

    const content = document.createElement('div');
    content.className = 'bb-card__content';

    const title = document.createElement('p');
    title.className = 'bb-card__title';
    title.textContent = product.title;

    const price = document.createElement('p');
    price.className = 'bb-card__price';
    price.dataset.bbCardPrice = '';
    const prices = variants.map((v) => v[3]);
    const min = prices.length ? Math.min(...prices) : 0;
    price.textContent = prices.length && Math.max(...prices) !== min ? `From ${money(min)}` : money(min);
    content.append(title, price);

    const actions = document.createElement('div');
    actions.className = 'bb-card__actions';

    if (mode === 'variant-list') {
      const o1Varies = new Set(variants.map((v) => v[1])).size > 1;
      const o2Varies = new Set(variants.map((v) => v[2])).size > 1;
      const labelFor = (v) => {
        const parts = [];
        if (o1Varies && v[1] && v[1] !== 'Default Title') parts.push(v[1]);
        if (o2Varies && v[2] && v[2] !== 'Default Title') parts.push(v[2]);
        return parts.join(' / ') || [v[1], v[2]].filter((x) => x && x !== 'Default Title').join(' / ') || 'Default';
      };
      // Toàn bộ dùng dropdown cho đồng bộ (bỏ pills).
      const select = document.createElement('select');
      select.className = 'bb-card__select';
      select.dataset.bbVariantSelect = '';
      select.setAttribute('aria-label', 'Choose option');
      for (const v of variants) {
        const opt = document.createElement('option');
        opt.value = String(v[0]);
        opt.textContent = labelFor(v);
        select.append(opt);
      }
      actions.append(select);
    }

    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'bb-card__add';
    add.dataset.bbAdd = '';
    add.disabled = true;
    add.textContent = this.addLabel;
    actions.append(add);
    content.append(actions);

    const json = document.createElement('script');
    json.type = 'application/json';
    json.dataset.bbVariants = '';
    json.textContent = JSON.stringify(variants);

    card.append(media, content, json);
    return card;
  }

  /* ---------- checkout ---------- */

  async #checkout() {
    if (this.state.bundles.length === 0) return this.#showError('Please complete at least one bundle.');
    // Chỉ add những bundle ĐỦ món mà CHƯA có trong giỏ (cartId null) -> tránh add trùng khi quay lại.
    const newOnes = [];
    this.state.bundles.forEach((bn, b) => {
      if (this.#isFull(bn) && !this.state.cartIds[b]) newOnes.push(b);
    });
    // Tất cả bundle đã nằm trong giỏ rồi -> mở trang GIỎ cho khách xem (KHÔNG nhảy checkout).
    if (newOnes.length === 0) {
      window.location.href = '/cart';
      return;
    }
    const button = this.querySelector('[data-bb-checkout]');
    button.disabled = true;
    this.#hideError();
    try {
      const items = [];
      const newIds = {}; // index bundle -> _bundle_id vừa tạo
      const isBxgy = this.pricingType === 'bxgy';
      const tiersStr = (this.extraTiers || []).join(',');
      newOnes.forEach((b) => {
        const bundle = this.state.bundles[b];
        const bundleId = `${Date.now()}-${b}-${Math.floor(Math.random() * 1e6)}`;
        newIds[b] = bundleId;
        const count = String(bundle.length);
        // _bundle_base + _bundle_tiers -> function tự tính ưu đãi theo SỐ bundle trong giỏ.
        const baseStr = (this.#calc(bundle, b).basePay / 100).toFixed(2);
        bundle.forEach((s, i) => {
          const props = {
            _bundle: this.bundleName,
            _bundle_id: bundleId,
            _bundle_count: count,
            _bundle_slot: String(i),
            _bundle_type: isBxgy ? 'bxgy' : 'fixed',
            _bundle_price: baseStr,
            // bxgy: base = tổng phần TÍNH TIỀN (đã trừ quà free, do #calc tính); fixed: giá cố định.
            _bundle_base: baseStr,
            _bundle_tiers: tiersStr,
          };
          // bxgy: đánh dấu slot QUÀ -> function 100% off slot đó; phần còn lại vẫn "buy more save more".
          if (isBxgy && this.freeSlots.includes(i)) props._bundle_free = 'yes';
          // Chỉ offer bật countdown mới có note -> cart hiện "limited time ends ...".
          if (this.limitedNote) props._bundle_ends = this.limitedNote;
          // Trang nguồn -> cart "Add bundle 2" redirect đúng offer. + nudge free-ship khi chỉ 1 bundle.
          props._bundle_url = window.location.pathname;
          if (this.fsNudge) {
            props._bundle_fs = this.fsNudge;
            props._bundle_fs_min = String(this.fsThreshold);
          }
          items.push({ id: s.variantId, quantity: 1, properties: props });
        });
      });

      const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message || 'Could not add items to cart.');

      // Đánh dấu các bundle vừa add là đã trong giỏ -> không add lại lần sau.
      newOnes.forEach((b) => { this.state.cartIds[b] = newIds[b]; });

      // Meta Pixel AddToCart — giá trị = tiền KHÁCH TRẢ của các bộ vừa thêm.
      let payCents = 0;
      const contentIds = [];
      const contents = [];
      newOnes.forEach((b) => {
        payCents += this.#calc(this.state.bundles[b], b).pay;
        this.state.bundles[b].forEach((s) => {
          contentIds.push(String(s.variantId));
          contents.push({ id: String(s.variantId), quantity: 1 });
        });
      });
      this.#track(
        'AddToCart',
        {
          content_type: 'product',
          content_ids: contentIds,
          contents,
          num_items: items.length,
          value: Number((payCents / 100).toFixed(2)),
          currency: this.currency,
        },
        true
      );

      this.#persist(); // GIỮ state (gồm cartIds) -> quay lại trang vẫn ở màn final
      document.dispatchEvent(
        new CartAddEvent({}, this.sectionId, { source: 'bundle-builder-component', itemCount: items.length })
      );
      // "Add to cart" -> đưa khách sang trang GIỎ để xem (không nhảy checkout, không đứng im).
      window.location.href = '/cart';
    } catch (error) {
      this.#showError(error.message);
    } finally {
      button.disabled = false;
    }
  }

  /* ---------- persistence & errors ---------- */

  #persist() {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify({ bundles: this.state.bundles, cartIds: this.state.cartIds, draft: this.state.draft, brand: this.selectedBrand }));
    } catch {
      /* non-critical */
    }
  }

  #restore() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
      if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return;
      const okArr = (a) => Array.isArray(a) && a.length === this.stepCount;
      if (Array.isArray(saved.bundles) && saved.bundles.every((bn) => okArr(bn) && bn.every(Boolean))) {
        this.state.bundles = saved.bundles;
        // cartIds song song bundles (id nếu đã trong giỏ); chuẩn hoá độ dài.
        const ids = Array.isArray(saved.cartIds) ? saved.cartIds : [];
        this.state.cartIds = this.state.bundles.map((_, i) => ids[i] || null);
      }
      if (okArr(saved.draft)) this.state.draft = saved.draft;
      if (typeof saved.brand === 'string') this.selectedBrand = saved.brand;
    } catch {
      /* start fresh */
    }
  }

  #showError(message) {
    const el = this.querySelector('[data-bb-error]');
    el.textContent = message;
    el.classList.remove('bb-hide');
  }
  #hideError() {
    this.querySelector('[data-bb-error]')?.classList.add('bb-hide');
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
